import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  memo,
  useMemo,
  useState,
} from 'react';

import {
  useDisplay,
  useTownAssets,
} from '../../stores';
import {
  casesStatusPresetOf,
  casesStatusPresets,
  getLayoutBoundingRect,
} from '../../utils';
import { Button } from '../buttons';
import {
  Column,
  Row,
} from '../display';
import { Checkbox } from '../input';
import SvgColorImg from '../SvgColorImg';

// The pictogram each standing wears in the status column of the rows
const standingIcons = {
  new: '/images/svg/gui/new2.svg',
  ongoing: '/images/svg/gui/ongoing.svg',
  done: '/images/svg/gui/checkThick.svg',
};

// Pick what a list shows: which standings (the automatic rule unless "custom" is
// checked, then exactly the checked ones), and which teams' items among the rows
// offered (rows that may not be picked stay listed, grayed). Renders as a panel
// anchored under the button identified by anchorId. Applying hands the choice to
// onApply(statuses, teams), resetting calls onReset; both close the panel
const ListFilterPopup = memo(
  ({
    anchorId,
    onClose,
    statuses: currentStatuses,
    teams: currentTeams,
    minimalTeams,
    rows,
    onApply,
    onReset,
  }) => {
    const { assets } = useTownAssets();
    const { translate } = assets;
    const { display } = useDisplay(false);
    const browserHeight = display?.browserHeight ?? globalThis.innerHeight;

    // Layout px, so the values stay valid as fixed top/left under the shell zoom
    const [panelStyle] = useState(() => {
      const anchorRect = getLayoutBoundingRect(document.getElementById(anchorId));
      const top = (anchorRect?.bottom ?? 0) + 4;
      return {
        top,
        left: anchorRect?.left ?? 0,
        maxHeight: browserHeight - top - 10,
      };
    });

    const allGpids = useMemo(
      () => rows.filter((row) => row.selectable).map((row) => row.group.grpGpid),
      [rows],
    );

    const [custom, setCustom] = useState(() => (casesStatusPresetOf(currentStatuses) !== 'active'));
    const [kinds, setKinds] = useState(
      () => new Set((casesStatusPresetOf(currentStatuses) === 'active')
        ? ['new', 'ongoing']
        : currentStatuses.filter((kind) => casesStatusPresets.custom.includes(kind))),
    );
    const [selected, setSelected] = useState(() => new Set(currentTeams));

    const statuses = (custom) ? [...kinds] : casesStatusPresets.active;

    const reset = () => {
      onReset();
      onClose();
    };

    const apply = () => {
      onApply(statuses, allGpids.filter((gpid) => selected.has(gpid)));
      onClose();
    };

    const toggle = (gpid) => {
      setSelected((current) => {
        const next = new Set(current);
        if (next.has(gpid)) next.delete(gpid);
        else next.add(gpid);
        return next;
      });
    };

    const toggleKind = (kind) => {
      setKinds((current) => {
        const next = new Set(current);
        if (next.has(kind)) next.delete(kind);
        else next.add(kind);
        return next;
      });
    };

    const allSelected = (allGpids.length > 0) && allGpids.every((gpid) => selected.has(gpid));
    const toggleAll = () => {
      setSelected(allSelected ? new Set(minimalTeams) : new Set(allGpids));
    };

    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,
         jsx-a11y/click-events-have-key-events */}
        <div
          className="z-50 fixed inset-0 bg-white/40"
          onClick={onClose}
        />

        <div
          className="z-50 bg-white fixed rounded-md border border-pxMainOutline shadow-lg overflow-y-auto"
          style={panelStyle}
        >
          <Column crossStretch gap={1} className="p-4 min-w-72">
            <div className="pb-2 font-semibold">
              {translate('selectItemsToDisplay', 'statements')}
            </div>
            <div className="pb-1 border-b border-pxCaseHeaderSecondaryBrdr">
              {translate('status')}
            </div>

            <Checkbox
              id="listStatusCustom"
              checked={custom}
              onChange={() => setCustom((current) => (!current))}
              label={translate('custom')}
            />
            {casesStatusPresets.custom.map((kind) => (
              <div key={kind} className={classNames('pl-6', { 'opacity-60': !custom })}>
                <Checkbox
                  id={`listStatus_${kind}`}
                  checked={kinds.has(kind)}
                  onChange={() => {
                    setCustom(true);
                    toggleKind(kind);
                  }}
                  label={(
                    <Row start gap={1}>
                      <SvgColorImg
                        src={standingIcons[kind]}
                        className="h-5 w-5 shrink-0 fill-pxIconMuted"
                        imgColorAsCssFill
                      />
                      <span>{translate(kind)}</span>
                    </Row>
                  )}
                />
              </div>
            ))}

            {(rows.length)
              ? (
                  <>
                    <div className="pt-3 pb-1 border-b border-pxCaseHeaderSecondaryBrdr">
                      {translate('displayedTeams')}
                    </div>

                    <Checkbox
                      id="teamsFilterAll"
                      checked={allSelected}
                      onChange={toggleAll}
                      label={translate('allTeams')}
                    />

                    {rows.map(({ group, depth, selectable }) => (
                      <div key={group.grpGpid} style={{ paddingLeft: `${depth}rem` }}>
                        <Checkbox
                          id={`teamsFilter_${group.grpGpid}`}
                          checked={selectable && selected.has(group.grpGpid)}
                          onChange={selectable ? () => toggle(group.grpGpid) : () => {}}
                          disabled={!selectable}
                          label={group.grpLbl ?? group.grpGpid}
                        />
                      </div>
                    ))}
                  </>
                )
              : null}

            <Row end gap={2} className="pt-3">
              <Button
                label={translate('reset')}
                onClick={reset}
                neutralColor
              />
              <Button
                label={translate('apply')}
                onClick={apply}
                disabled={custom && (!kinds.size)}
                baseColor
              />
            </Row>
          </Column>
        </div>
      </>
    );
  },
);

ListFilterPopup.displayName = 'ListFilterPopup';

ListFilterPopup.propTypes = {
  anchorId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  // Teams currently picked, and the ones that stay picked when all are unticked
  teams: PropTypes.arrayOf(PropTypes.string).isRequired,
  minimalTeams: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({
    group: PropTypes.shape({
      grpGpid: PropTypes.string.isRequired,
      grpLbl: PropTypes.string,
    }).isRequired,
    depth: PropTypes.number,
    selectable: PropTypes.bool,
  })).isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default ListFilterPopup;
